export async function copyTextToClipboard(text: string) {
  text = text.replace(/^\s+|\s+$/g, '');
  if ('clipboard' in navigator) {
    return await navigator.clipboard.writeText(text);
  } else {
    return document.execCommand('copy', true, text);
  }
}

/**
 * Copy markdown as rich text (HTML) to clipboard.
 * Pastes as formatted text in Google Docs, Word, email, etc.
 * Falls back to plain text if rich copy is unsupported.
 */
export async function copyRichTextToClipboard(markdown: string) {
  const trimmed = markdown.replace(/^\s+|\s+$/g, '');

  // Strip image markdown since images can't be pasted as rich text
  const withoutImages = trimmed.replace(/!\[.*?\]\(.*?\)\n*/g, '');

  // Convert markdown to HTML using micromark (already installed)
  const { micromark } = await import('micromark');
  const { gfm, gfmHtml } = await import('micromark-extension-gfm');

  const html = micromark(withoutImages, {
    extensions: [gfm()],
    htmlExtensions: [gfmHtml()],
  });

  // Plain text fallback: strip markdown syntax
  const plainText = withoutImages
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/`(.*?)`/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^[-*]\s+/gm, '• ')
    .replace(/^---$/gm, '')
    .trim();

  if ('clipboard' in navigator && typeof ClipboardItem !== 'undefined') {
    try {
      const item = new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
      });
      return await navigator.clipboard.write([item]);
    } catch {
      // Fall back to plain text
      return await navigator.clipboard.writeText(plainText);
    }
  }

  return await copyTextToClipboard(plainText);
}
